/**
 * `NavItem` - one row in the sidebar navigation.
 *
 * Geometry and colour per the design handoff "Layout / shell" section:
 * 13.5px/500 label, 7px/10px padding, 6px radius, a 6px leading dot, active
 * row filled with `--side-hi` and lifted to `--side-tx-hi`. Hover matches
 * active so the target reads as clickable on a dark surface.
 *
 * The leading dot carries a third state the handoff calls for: modules that
 * are implemented but not current get a muted dot (`--side-dot`), modules
 * with only a placeholder screen get no dot at all. That gives staff a
 * standing signal of what is actually built without a second label.
 *
 * Accessibility:
 *   - Renders a `NavLink`, so it is a real link: keyboard-reachable, and
 *     `aria-current="page"` is set on the active route by react-router.
 *   - The dot is `aria-hidden` - it duplicates state already conveyed by
 *     `aria-current` and, for placeholders, by the destination screen. Colour
 *     is never the only signal.
 *   - Prefix matching is intentional: `/members` stays active on
 *     `/members/:id` so the sidebar does not appear to lose its place when a
 *     member is opened.
 */
import { NavLink } from 'react-router-dom'

import { FOCUS_RING_ON_DARK_CLASS } from './focus-ring'

export interface NavItemProps {
  readonly to: string
  readonly label: string
  /** True when the route serves a real screen rather than a placeholder. */
  readonly implemented: boolean
}

export function NavItem({ to, label, implemented }: NavItemProps): JSX.Element {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        [
          'flex items-center gap-[10px] rounded-md px-[10px] py-[7px] text-murmuration-nav no-underline',
          'transition-colors duration-murmuration-fast hover:bg-side-hi hover:text-side-tx-hi hover:no-underline',
          isActive ? 'bg-side-hi text-side-tx-hi' : 'text-side-tx',
          FOCUS_RING_ON_DARK_CLASS,
        ].join(' ')
      }
    >
      {({ isActive }) => (
        <>
          <span
            aria-hidden="true"
            data-state={isActive ? 'active' : implemented ? 'implemented' : 'planned'}
            className={[
              'h-[6px] w-[6px] flex-none rounded-full',
              isActive ? 'bg-brand-primary' : implemented ? 'bg-side-dot' : 'bg-transparent',
            ].join(' ')}
          />
          {label}
        </>
      )}
    </NavLink>
  )
}
